import { Alert, AlertTitle, Stack } from "@mui/material";

interface ChatAccessErrorBannerProps {
  /**
   * When true, render the "not found or no access" (HTTP 404) copy. The
   * backend deliberately returns the same 404 for missing dialogues and other
   * users' dialogues (#30212), so the copy covers both without confirming
   * which one it is. Otherwise render a generic "couldn't load this chat"
   * message for other failures.
   */
  notFound?: boolean;
}

/**
 * Full-page error state shown on the chat page when a dialogue can't be loaded.
 * Distinguishes "this chat doesn't exist or isn't yours" (404) from a generic
 * load failure so visitors get an actionable message instead of a bare
 * "Error fetching config".
 */
const ChatAccessErrorBanner = ({
  notFound = false,
}: ChatAccessErrorBannerProps) => {
  return (
    <Stack
      sx={{ height: "calc(100vh - 97px)", overflow: "hidden", px: 2, pt: 2 }}
      justifyContent="flex-start"
      alignItems="stretch"
    >
      <Alert
        severity={notFound ? "warning" : "error"}
        sx={{
          width: "100%",
          borderRadius: 2,
          border: 1,
          borderColor: "grey.300",
        }}
      >
        <AlertTitle>
          {notFound ? "Chat not found" : "Couldn't load this chat"}
        </AlertTitle>
        {notFound
          ? "This chat doesn't exist or you don't have access to it. If someone sent you this link, ask them to enable sharing for the conversation."
          : "Something went wrong while loading this conversation. Please refresh the page or try again later."}
      </Alert>
    </Stack>
  );
};

export default ChatAccessErrorBanner;
